import { collection, getDocs, query, where, doc, getDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { subnetNames } from '../../utils/subnetNames';
import { subnetDescriptions } from '../../utils/subnetDescriptions';

// Cache for Twitter users to avoid repeated API calls
const delegationCache: Record<string, {
  data: any[],
  timestamp: number
}> = {};

// Cache expiration time (5 minutes)
const CACHE_EXPIRATION = 5 * 60 * 1000;

// Map company names to their associated subnets and Twitter info
const companyToSubnets: Record<string, {
  subnets: number[];
  twitterUsername: string;
  aliases?: string[]; // Alternative names for matching
}> = {
  'rayon labs': {
    subnets: [19, 56, 64], // Nineteen, Gradients, Chutes
    twitterUsername: 'rayon_labs',
    aliases: ['rayonlabs', 'rayon']
  },
  'macrocosmos': {
    subnets: [1, 9, 13, 25, 37], // Apex, Pretraining, Data Universe, Protein Folding, Finetuning
    twitterUsername: 'MacrocosmosAI',
    aliases: ['macrocosm', 'macrocosmos ai']
  },
  'natix': {
    subnets: [72], // StreetVision
    twitterUsername: 'NATIXNetwork',
    aliases: ['natix network', 'streetvision']
  }
};

// Common blockchain and crypto terms to help identify subjects
const CRYPTO_TERMS = new Set([
  'tao', 'tau', 'taoshi', 'taofi', 'bittensor', 'btensor',
  'stake', 'staking', 'validator', 'validation',
  'miner', 'mining', 'delegate', 'delegation',
  'alpha', 'subnet', 'network', 'blockchain',
  'token', 'crypto', 'cryptocurrency', 'defi',
  'protocol', 'platform', 'dao', 'governance'
]);

function getTweetRelevanceScore(tweet: any, searchTerms: string[], subnetId?: number, companyTwitterUsername?: string): number {
  let score = 0;
  const tweetText = tweet.text.toLowerCase();
  
  // CRITICAL: At least one search term MUST be present in the tweet as a whole word
  const hasSearchTerm = searchTerms.some(term => {
    const termRegex = new RegExp(`\\b${term.toLowerCase()}\\b`, 'i');
    return termRegex.test(tweetText);
  });
  
  if (!hasSearchTerm) {
    return 0; // Immediately reject tweets without any search terms
  }
  
  // CRITICAL: If we're looking for a specific subnet, the tweet MUST mention it
  if (subnetId !== undefined) {
    const subnetName = subnetNames[subnetId]?.toLowerCase() || '';
    const subnetMentions = [
      `subnet ${subnetId}`,
      `sn${subnetId}`,
      `subnet${subnetId}`,
      `subnet #${subnetId}`,
      `sn #${subnetId}`,
      `sn-${subnetId}`,
      subnetName,
      // Add variations of the subnet name
      ...subnetName.split(/[\s-]+/).filter(part => part.length > 2)
    ];
    
    // Special case for StreetVision (SN72)
    if (subnetId === 72) {
      subnetMentions.push('streetvision', 'street vision', 'natix');
    }
    
    // Check for exact word matches
    const hasSubnetMention = subnetMentions.some(mention => {
      const mentionRegex = new RegExp(`\\b${mention}\\b`, 'i');
      return mentionRegex.test(tweetText);
    });
    
    if (!hasSubnetMention) {
      return 0;
    }
    
    // Add score for each mention
    subnetMentions.forEach(mention => {
      const mentionRegex = new RegExp(`\\b${mention}\\b`, 'i');
      if (mentionRegex.test(tweetText)) {
        score += 20;
      }
    });
  }

  // High priority: Direct posts from company
  if (companyTwitterUsername && tweet.username?.toLowerCase() === companyTwitterUsername.toLowerCase()) {
    score += 25;
  }

  // Medium priority: Mentions of company Twitter handle
  if (companyTwitterUsername && tweetText.includes(`@${companyTwitterUsername.toLowerCase()}`)) {
    score += 15;
  }

  // Check each search term for context and relevance
  searchTerms.forEach(term => {
    const termRegex = new RegExp(`\\b${term.toLowerCase()}\\b`, 'i');
    if (termRegex.test(tweetText)) {
      // Base score for term presence
      score += 10;
      
      // Get the sentence containing the term
      const sentences = tweetText.split(/[.!?]\s+/);
      const termSentence = sentences.find(s => termRegex.test(s));
      
      if (termSentence) {
        // Check if the term is the subject of the sentence
        if (termSentence.indexOf(term.toLowerCase()) <= termSentence.length * 0.3) {
          score += 15; // High bonus for terms appearing as subjects
        }
        
        // Check for crypto context in the same sentence
        const cryptoTermsInSentence = Array.from(CRYPTO_TERMS).filter(ct => {
          const ctRegex = new RegExp(`\\b${ct}\\b`, 'i');
          return ctRegex.test(termSentence);
        });
        
        score += cryptoTermsInSentence.length * 3;
      }
      
      // Additional score if the term appears in the first sentence
      if (termRegex.test(sentences[0])) {
        score += 8;
      }
      
      // Check surrounding context (50 chars before and after)
      const termIndex = tweetText.indexOf(term.toLowerCase());
      const context = tweetText.substring(
        Math.max(0, termIndex - 50),
        Math.min(tweetText.length, termIndex + 50)
      );
      
      // Count unique crypto terms in context
      const uniqueCryptoTerms = new Set(
        Array.from(CRYPTO_TERMS).filter(ct => {
          const ctRegex = new RegExp(`\\b${ct}\\b`, 'i');
          return ctRegex.test(context);
        })
      );
      
      score += uniqueCryptoTerms.size * 2;
    }
    
    // Bonus for hashtag matches
    if (tweetText.includes(`#${term.toLowerCase()}`)) {
      score += 15;
    }
  });

  // Recency scoring
  const tweetAge = Date.now() - new Date(tweet.created_at).getTime();
  const hoursOld = tweetAge / (1000 * 60 * 60);
  if (hoursOld < 6) {
    score += 25; // Very recent
  } else if (hoursOld < 24) {
    score += 20;
  } else if (hoursOld < 48) {
    score += 15;
  } else if (hoursOld < 72) {
    score += 10;
  } else if (hoursOld < 168) {
    score += 5;
  }

  // Engagement scoring
  if (tweet.retweet_count > 50) score += 15;
  else if (tweet.retweet_count > 20) score += 10;
  else if (tweet.retweet_count > 5) score += 5;

  if (tweet.like_count > 100) score += 15;
  else if (tweet.like_count > 50) score += 10;
  else if (tweet.like_count > 20) score += 5;

  if (tweet.reply_count > 20) score += 10;
  else if (tweet.reply_count > 10) score += 5;
  else if (tweet.reply_count > 5) score += 3;

  // Media and links
  if (tweet.media_urls?.length > 0) score += 10;
  if (tweetText.includes('http')) score += 5;

  // Content quality
  if (tweetText.length > 200) {
    score += 10;
    if (tweetText.includes('🧵') || tweetText.includes('1/')) {
      score += 5;
    }
  } else if (tweetText.length > 100) {
    score += 5;
  }

  // CRITICAL: Minimum relevance threshold
  // If the tweet doesn't have a high enough score, it's probably not relevant enough
  return score >= 30 ? score : 0;
}

export function extractTimeRange(query: string): { hours: number, description: string } {
  const lowerQuery = query.toLowerCase();
  
  if (lowerQuery.includes('week') || lowerQuery.includes('7 day')) {
    return { hours: 168, description: 'last 7 days' };
  }
  if (lowerQuery.includes('month') || lowerQuery.includes('30 day')) {
    return { hours: 720, description: 'last 30 days' };
  }
  if (lowerQuery.includes('hour')) {
    const hourMatch = lowerQuery.match(/(\d+)\s*hour/);
    if (hourMatch) {
      const hours = parseInt(hourMatch[1]);
      return { hours, description: `last ${hours} hours` };
    }
  }
  return { hours: 24, description: 'last 24 hours' };
}

export function extractListParams(query: string): { count: number, type: 'gainers' | 'losers' } {
  const lowerQuery = query.toLowerCase();
  
  // First try to find explicit number mentions
  const numberWords = {
    'one': 1, 'two': 2, 'three': 3, 'four': 4, 'five': 5,
    'six': 6, 'seven': 7, 'eight': 8, 'nine': 9, 'ten': 10,
    'fifteen': 15, 'twenty': 20
  };
  
  // Look for number patterns
  const patterns = [
    // Direct numbers: "10 gainers", "top 5"
    /(?:top|show|get|list)?\s*(\d+)\s*(?:subnet|gain|loss|gainer|loser|movers)/i,
    // Number words: "five gainers", "top ten"
    new RegExp(`(?:top|show|get|list)?\\s*(${Object.keys(numberWords).join('|')})\\s*(?:subnet|gain|loss|gainer|loser|movers)`, 'i'),
    // Fallback for just numbers: "10", "5"
    /^(\d+)$/
  ];
  
  let count = 5; // Default count
  
  // Try each pattern
  for (const pattern of patterns) {
    const match = lowerQuery.match(pattern);
    if (match) {
      const value = match[1].toLowerCase();
      if (numberWords[value]) {
        count = numberWords[value];
      } else {
        const parsed = parseInt(value);
        if (!isNaN(parsed) && parsed > 0) {
          count = Math.min(parsed, 50); // Cap at 50 to prevent excessive results
        }
      }
      break;
    }
  }
  
  // Determine type based on keywords
  const type = lowerQuery.match(/loss|loser|drop|decline|down/i) ? 'losers' : 'gainers';
  
  return { count, type };
}

export function extractSubnets(query: string): number[] {
  const subnets = new Set<number>();
  const words = query.toLowerCase().split(/[\s,]+/);
  
  // First check for explicit subnet numbers
  const subnetNumberMatches = query.match(/(?:subnet|sn)?\s*(\d+)/gi);
  if (subnetNumberMatches) {
    subnetNumberMatches.forEach(match => {
      const id = parseInt(match.replace(/[^\d]/g, ''));
      if (id > 0 && id in subnetNames) {
        subnets.add(id);
      }
    });
  }

  // Check for subnet names (exact word matches)
  Object.entries(subnetNames).forEach(([id, name]) => {
    // Skip Root subnet (0)
    if (id === '0') return;
    
    const nameRegex = new RegExp(`\\b${name.toLowerCase()}\\b`, 'i');
    if (nameRegex.test(query.toLowerCase())) {
      subnets.add(parseInt(id));
    }
  });
  
  // Special case for StreetVision (SN72)
  if (query.toLowerCase().includes('streetvision') || 
      query.toLowerCase().includes('street vision') || 
      query.toLowerCase().includes('natix')) {
    subnets.add(72);
  }
  
  // Check if this is a company query
  const companyMatch = findCompanySubnets(query);
  if (companyMatch) {
    companyMatch.subnets.forEach(id => subnets.add(id));
  }
  
  return Array.from(subnets);
}

export function findCompanySubnets(query: string): { 
  companyName: string; 
  subnets: number[];
  description?: string;
} | null {
  const lowerQuery = query.toLowerCase();
  
  for (const [name, info] of Object.entries(companyToSubnets)) {
    if (lowerQuery.includes(name) || info.aliases?.some(alias => {
      const aliasRegex = new RegExp(`\\b${alias}\\b`, 'i');
      return aliasRegex.test(lowerQuery);
    })) {
      return {
        companyName: name,
        subnets: info.subnets,
        description: info.subnets[0] ? subnetDescriptions[info.subnets[0]]?.description : undefined
      };
    }
  }
  
  return null;
}

export async function getRecentTweets(companyNameOrSubnet: string | number, query?: string): Promise<any[]> {
  try {
    let searchTerms: string[] = [];
    let subnetNumber: number | undefined;
    let subnetName: string | undefined;
    let companyTwitterUsername: string | undefined;

    // Extract keywords from the query
    const keywords = query ? extractKeywords(query) : [];

    if (typeof companyNameOrSubnet === 'number') {
      subnetNumber = companyNameOrSubnet;
      subnetName = subnetNames[companyNameOrSubnet];
      if (subnetName) {
        searchTerms = generateSubnetSearchTerms(companyNameOrSubnet, subnetName);
      }
    } else if (companyNameOrSubnet === 'all') {
      // For general queries, use the extracted keywords
      searchTerms = keywords;
      
      // If searching for a specific term, make sure it's included
      if (query && !query.toLowerCase().includes('what') && !query.toLowerCase().includes('how')) {
        const mainSubject = query.toLowerCase().split(/\s+/)[0];
        if (mainSubject && mainSubject.length > 2) {
          searchTerms.push(mainSubject);
        }
      }
    } else {
      const companyInfo = findCompanyInfo(companyNameOrSubnet.toLowerCase());
      if (companyInfo) {
        companyTwitterUsername = companyInfo.twitterUsername;
        searchTerms = [companyInfo.name, ...companyInfo.aliases || []];
      } else {
        const match = findBestSubnetMatch(companyNameOrSubnet);
        if (match !== null) {
          subnetNumber = match;
          subnetName = subnetNames[match];
          searchTerms = generateSubnetSearchTerms(match, subnetName);
        }
      }
    }

    // Add any additional keywords to search terms
    if (keywords.length > 0) {
      searchTerms = [...new Set([...searchTerms, ...keywords])];
    }

    // Get all tweets from the last 7 days
    const tweetsRef = collection(db, 'subnet_tweets', 'tweets', 'all');
    const snapshot = await getDocs(tweetsRef);

    // Get all potentially relevant tweets
    let relevantTweets = snapshot.docs
      .map(doc => {
        const data = doc.data();
        const created_at = data.created_at instanceof Timestamp ? 
          data.created_at.toDate() : 
          new Date(data.created_at);
        
        if (!created_at || isNaN(created_at.getTime())) {
          return null;
        }
        
        return {
          id: doc.id,
          ...data,
          created_at
        };
      })
      .filter(tweet => tweet !== null);

    // Score and filter tweets
    relevantTweets = relevantTweets
      .map(tweet => ({
        ...tweet,
        relevanceScore: getTweetRelevanceScore(tweet, searchTerms, subnetNumber, companyTwitterUsername)
      }))
      .filter(tweet => tweet.relevanceScore > 0) // Only keep tweets with any relevance
      .sort((a, b) => b.relevanceScore - a.relevanceScore);

    // Filter out low-value content and ensure subject presence
    return relevantTweets.filter(tweet => {
      const tweetText = tweet.text.toLowerCase();
      
      // For specific subject queries, ensure the subject is present
      if (companyNameOrSubnet !== 'all' && searchTerms.length > 0) {
        const hasSubject = searchTerms.some(term => {
          const termRegex = new RegExp(`\\b${term.toLowerCase()}\\b`, 'i');
          return termRegex.test(tweetText);
        });
        if (!hasSubject) return false;
      }
      
      // Skip retweets unless they explicitly mention search terms
      if (tweetText.startsWith('rt @')) {
        return searchTerms.some(term => {
          const termRegex = new RegExp(`\\b${term.toLowerCase()}\\b`, 'i');
          return termRegex.test(tweetText);
        });
      }
      
      return true;
    });
  } catch (error) {
    console.error('Error fetching tweets:', error);
    return [];
  }
}

function findCompanyInfo(searchTerm: string): { 
  name: string;
  subnets: number[];
  twitterUsername: string;
  aliases?: string[];
} | null {
  const normalizedSearch = searchTerm.toLowerCase();
  
  for (const [name, info] of Object.entries(companyToSubnets)) {
    if (normalizedSearch.includes(name) || info.aliases?.some(alias => {
      const aliasRegex = new RegExp(`\\b${alias}\\b`, 'i');
      return aliasRegex.test(normalizedSearch);
    })) {
      return { name, ...info };
    }
  }
  
  return null;
}

function findBestSubnetMatch(searchTerm: string): number | null {
  const lowerTerm = searchTerm.toLowerCase();
  
  // Special case for StreetVision (SN72)
  if (lowerTerm.includes('streetvision') || lowerTerm.includes('street vision') || lowerTerm.includes('natix')) {
    return 72;
  }
  
  // Try exact match with subnet name
  for (const [id, name] of Object.entries(subnetNames)) {
    // Skip Root subnet (0)
    if (id === '0') continue;
    
    const nameRegex = new RegExp(`\\b${name.toLowerCase()}\\b`, 'i');
    if (nameRegex.test(lowerTerm)) {
      return parseInt(id);
    }
  }
  
  // Try partial match
  for (const [id, name] of Object.entries(subnetNames)) {
    // Skip Root subnet (0)
    if (id === '0') continue;
    
    if (name.toLowerCase().includes(lowerTerm)) {
      return parseInt(id);
    }
  }
  
  return null;
}

function generateSubnetSearchTerms(netuid: number, name: string): string[] {
  const terms = [
    `subnet ${netuid}`,
    `sn${netuid}`,
    `subnet${netuid}`,
    `subnet #${netuid}`,
    `sn #${netuid}`,
    `sn-${netuid}`,
    name.toLowerCase()
  ];

  // Special case for StreetVision (SN72)
  if (netuid === 72) {
    terms.push('streetvision', 'street vision', 'natix', 'natix network');
  }

  // Add variations of the subnet name
  const nameParts = name.toLowerCase().split(/[\s-]+/);
  if (nameParts.length > 1) {
    terms.push(...nameParts.filter(part => part.length > 2));
  }

  // Add any company-specific terms
  for (const [company, info] of Object.entries(companyToSubnets)) {
    if (info.subnets.includes(netuid)) {
      terms.push(company.toLowerCase());
      if (info.aliases) {
        terms.push(...info.aliases);
      }
    }
  }

  return [...new Set(terms)];
}

export function extractKeywords(query: string, isSubnetQuery: boolean = false): string[] {
  // Only use stop words for non-subnet queries
  const stopWords = isSubnetQuery ? new Set() : new Set([
    'the', 'is', 'at', 'which', 'on', 'in', 'a', 'an', 'and', 'or', 'but',
    'what', 'when', 'where', 'who', 'why', 'how', 'any', 'are', 'as', 'be',
    'by', 'for', 'from', 'has', 'he', 'she', 'it', 'its', 'of', 'that', 'to',
    'was', 'were', 'will', 'with', 'this', 'they', 'have', 'had',
    'tell', 'me', 'about', 'show', 'get', 'give', 'do', 'does', 'did',
    'latest', 'recent', 'new', 'news', 'update', 'updates'
  ]);
  
  // Split query into words and clean them
  const words = query.toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')  // Remove special characters
    .split(/\s+/)                   // Split on whitespace
    .filter(word => 
      word.length > 2 &&           // Word must be longer than 2 characters
      !stopWords.has(word) &&      // Ignore stop words
      !word.match(/^\d+$/)         // Ignore numbers-only words
    );
  
  // Add any multi-word phrases that might be important
  const phrases = [];
  for (let i = 0; i < words.length - 1; i++) {
    const phrase = words[i] + ' ' + words[i + 1];
    if (phrase.length > 5) {  // Only add meaningful phrases
      phrases.push(phrase);
    }
  }
  
  return [...new Set([...words, ...phrases])];  // Remove duplicates
}